// src/lib/ShogiEngine.js
// Framework'ten bağımsız şogi kural motoru (JSA kuralları).
//
// Koordinatlar { row, col }, 0–8. row 0 = üst (gote tarafı), sente alttan oynar ve
// ileri = row azalır. Terfi bölgesi: sente için row <= 2, gote için row >= 6.
//
// İç temsil (hız için): 81 elemanlı Int8Array. 0 = boş, sente taşları pozitif,
// gote taşları negatif kod. Kodlar: P1 L2 N3 S4 G5 B6 R7 K8, terfili = taban + 8
// (+P9 +L10 +N11 +S12 +B14 +R15). Kare indeksi = row * 9 + col.
// Elden atma eli: [sente, gote] için Int8Array(8), indeks = taş kodu (1..7).
//
// İç hamle kodu (tamsayı): from | (to << 7) | (promote << 14)
//   from 0..80 = tahtadan hareket, 81 + tip (82..88) = elden atma.
//
// Genel API (moves/getLegalDrops/dropTargets sözde-yasaldır, geriye uyum için):
//   board() hands() turn() history() moves() move() drop() getLegalDrops()
//   dropTargets() isCheck() hasAnyLegalMoves() unpromotedType()
// Yasal API: legalMoves() legalDrops() allLegalMoves() isCheckmate() isLegal()
//   applyDescriptor() undo() clone() loadSfen() toSfen() Shogi.fromSfen()
//   parseUsi() toUsi() isSquareAttacked()

export const PIECE_NAMES = {
  P: '歩', // Pawn (Fuhyō)
  L: '香', // Lance (Kyōsha)
  N: '桂', // Knight (Keima)
  S: '銀', // Silver General (Ginshō)
  G: '金', // Gold General (Kinshō)
  B: '角', // Bishop (Kakugyō)
  R: '飛', // Rook (Hishyō)
  K: '玉', // King (Gyokushō)
};

export const PROMOTED_NAMES = {
  P: 'と', // Tokin (Promoted Pawn)
  L: '杏', // Promoted Lance
  N: '圭', // Promoted Knight
  S: '全', // Promoted Silver
  B: '馬', // Dragon Horse (Promoted Bishop)
  R: '龍', // Dragon (Promoted Rook)
};

export const START_SFEN = 'lnsgkgsnl/1r5b1/ppppppppp/9/9/9/PPPPPPPPP/1B5R1/LNSGKGSNL b - 1';

export const LETTERS = ['', 'P', 'L', 'N', 'S', 'G', 'B', 'R', 'K'];
export const TYPE_INDEX = { P: 1, L: 2, N: 3, S: 4, G: 5, B: 6, R: 7, K: 8 };
export const PROM = 8;
export const HAND_TYPES = ['P', 'L', 'N', 'S', 'G', 'B', 'R'];
const SFEN_HAND_ORDER = [7, 6, 5, 4, 3, 2, 1]; // R B G S N L P

// Taban tip (terfisiz) -> 1..8
export const baseType = (t) => (t > 8 ? t - 8 : t);
// Terfi edebilir mi? (terfisiz P L N S B R)
export const isPromotable = (t) => t <= 7 && t !== 5;

// ---- Hareket tabloları (ileri-normalleştirilmiş: fr = +1 ileri) ----
const GOLD_STEPS = [[1, -1], [1, 0], [1, 1], [0, -1], [0, 1], [-1, 0]];
const KING_STEPS = [[1, -1], [1, 0], [1, 1], [0, -1], [0, 1], [-1, -1], [-1, 0], [-1, 1]];
const DIAG = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
const ORTH = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const STEPS = [];
const SLIDES = [];
for (let t = 0; t <= 15; t++) {
  STEPS[t] = [];
  SLIDES[t] = [];
}
STEPS[1] = [[1, 0]];
SLIDES[2] = [[1, 0]];
STEPS[3] = [[2, -1], [2, 1]];
STEPS[4] = [[1, -1], [1, 0], [1, 1], [-1, -1], [-1, 1]];
STEPS[5] = GOLD_STEPS;
SLIDES[6] = DIAG;
SLIDES[7] = ORTH;
STEPS[8] = KING_STEPS;
STEPS[9] = GOLD_STEPS;
STEPS[10] = GOLD_STEPS;
STEPS[11] = GOLD_STEPS;
STEPS[12] = GOLD_STEPS;
STEPS[14] = ORTH;
SLIDES[14] = DIAG;
STEPS[15] = DIAG;
SLIDES[15] = ORTH;

// STEP_HIT[t*9 + (fr+1)*3 + (dc+1)] : t tipli taş komşu kareye (fr, dc) yönünde saldırır mı?
export const STEP_HIT = new Uint8Array(16 * 9);
for (let t = 1; t <= 15; t++) {
  for (let fr = -1; fr <= 1; fr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (fr === 0 && dc === 0) continue;
      let hit = false;
      if (t === 1 || t === 2) hit = fr === 1 && dc === 0;
      else if (t === 3) hit = false;
      else if (t === 4) hit = fr === 1 || (fr === -1 && dc !== 0);
      else if (t === 5 || (t >= 9 && t <= 12)) hit = fr === 1 || fr === 0 || (fr === -1 && dc === 0);
      else if (t === 6) hit = fr !== 0 && dc !== 0;
      else if (t === 7) hit = fr === 0 || dc === 0;
      else if (t === 8 || t === 14 || t === 15) hit = true;
      STEP_HIT[t * 9 + (fr + 1) * 3 + (dc + 1)] = hit ? 1 : 0;
    }
  }
}
const RAY_DIRS = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];

// ---- Zobrist anahtarları (deterministik PRNG; worker ile ana thread aynı) ----
let seed = 0x9e3779b9;
const rnd32 = () => {
  seed ^= seed << 13;
  seed ^= seed >>> 17;
  seed ^= seed << 5;
  return seed | 0;
};
const ZP_LO = new Int32Array(81 * 32);
const ZP_HI = new Int32Array(81 * 32);
for (let i = 0; i < 81 * 32; i++) {
  ZP_LO[i] = rnd32();
  ZP_HI[i] = rnd32();
}
const ZH_LO = new Int32Array(2 * 8 * 20);
const ZH_HI = new Int32Array(2 * 8 * 20);
for (let i = 0; i < 2 * 8 * 20; i++) {
  if (i % 20 === 0) continue; // sayı 0 => anahtar 0
  ZH_LO[i] = rnd32();
  ZH_HI[i] = rnd32();
}
const ZS_LO = rnd32();
const ZS_HI = rnd32();
const zpi = (sq, code) => sq * 32 + code + 16;
const zhi = (si, t, n) => (si * 8 + t) * 20 + n;

export const encodeMove = (from, to, promote) => from | (to << 7) | (promote ? 1 << 14 : 0);
export const moveFrom = (m) => m & 127;
export const moveTo = (m) => (m >> 7) & 127;
export const movePromote = (m) => (m >> 14) & 1;

const colorOf = (side) => (side > 0 ? 'sente' : 'gote');
const sideOf = (color) => (color === 'gote' ? -1 : 1);
const inZone = (side, row) => (side > 0 ? row <= 2 : row >= 6);

// Taşın bu satıra terfisiz gitmesi yasak mı (hareketsiz kalacak taş)?
const isDeadSquare = (t, side, row) => {
  if (t === 1 || t === 2) return side > 0 ? row === 0 : row === 8;
  if (t === 3) return side > 0 ? row <= 1 : row >= 7;
  return false;
};

export class Shogi {
  constructor(sfen) {
    this._sq = new Int8Array(81);
    this._hand = [new Int8Array(8), new Int8Array(8)];
    this._king = new Int16Array([-1, -1]);
    this._stm = 1;
    this._hlo = 0;
    this._hhi = 0;
    this._stack = [];
    this._moveHistory = [];
    this._moveNo0 = 1;
    if (sfen) this.loadSfen(sfen);
    else this.reset();
  }

  static fromSfen(sfen) {
    return new Shogi(sfen);
  }

  reset() {
    this.loadSfen(START_SFEN);
  }

  clone() {
    const c = Object.create(Shogi.prototype);
    c._sq = new Int8Array(this._sq);
    c._hand = [new Int8Array(this._hand[0]), new Int8Array(this._hand[1])];
    c._king = new Int16Array(this._king);
    c._stm = this._stm;
    c._hlo = this._hlo;
    c._hhi = this._hhi;
    c._stack = this._stack.slice();
    c._moveHistory = this._moveHistory.map((r) => ({ ...r }));
    c._moveNo0 = this._moveNo0;
    return c;
  }

  // ------------------------------------------------------------------ SFEN

  loadSfen(sfen) {
    let s = String(sfen || '').trim();
    if (s.startsWith('sfen ')) s = s.slice(5).trim();
    if (s === 'startpos') s = START_SFEN;
    const parts = s.split(/\s+/);
    if (parts.length < 2) throw new Error('Geçersiz SFEN: ' + sfen);
    const [boardPart, sidePart, handPart = '-', moveNoPart = '1'] = parts;
    const sq = new Int8Array(81);
    const ranks = boardPart.split('/');
    if (ranks.length !== 9) throw new Error('Geçersiz SFEN tahtası (9 sıra olmalı)');
    const king = [-1, -1];
    for (let r = 0; r < 9; r++) {
      let c = 0;
      let promoted = false;
      for (const ch of ranks[r]) {
        if (ch >= '1' && ch <= '9') {
          if (promoted) throw new Error('Geçersiz SFEN: + sonrası rakam');
          c += Number(ch);
          continue;
        }
        if (ch === '+') {
          promoted = true;
          continue;
        }
        const t = TYPE_INDEX[ch.toUpperCase()];
        if (!t || c > 8) throw new Error('Geçersiz SFEN karakteri: ' + ch);
        if (promoted && !isPromotable(t)) throw new Error('Terfi edemeyen taş: +' + ch);
        const side = ch === ch.toUpperCase() ? 1 : -1;
        const code = (promoted ? t + PROM : t) * side;
        sq[r * 9 + c] = code;
        if (t === 8) {
          const si = side > 0 ? 0 : 1;
          if (king[si] >= 0) throw new Error('Geçersiz SFEN: birden fazla şah');
          king[si] = r * 9 + c;
        }
        promoted = false;
        c++;
      }
      if (c !== 9) throw new Error('Geçersiz SFEN: sıra ' + (r + 1) + ' 9 kare değil');
    }
    if (sidePart !== 'b' && sidePart !== 'w') throw new Error('Geçersiz SFEN: sıra b/w olmalı');
    const hand = [new Int8Array(8), new Int8Array(8)];
    if (handPart !== '-') {
      let num = '';
      for (const ch of handPart) {
        if (ch >= '0' && ch <= '9') {
          num += ch;
          continue;
        }
        const t = TYPE_INDEX[ch.toUpperCase()];
        if (!t || t === 8) throw new Error('Geçersiz SFEN eli: ' + ch);
        const si = ch === ch.toUpperCase() ? 0 : 1;
        hand[si][t] += num ? Number(num) : 1;
        num = '';
      }
    }
    this._sq = sq;
    this._hand = hand;
    this._king = new Int16Array(king);
    this._stm = sidePart === 'b' ? 1 : -1;
    this._stack = [];
    this._moveHistory = [];
    this._moveNo0 = Math.max(1, parseInt(moveNoPart, 10) || 1);
    this._recomputeHash();
    return this;
  }

  toSfen() {
    const rows = [];
    for (let r = 0; r < 9; r++) {
      let out = '';
      let empty = 0;
      for (let c = 0; c < 9; c++) {
        const p = this._sq[r * 9 + c];
        if (!p) {
          empty++;
          continue;
        }
        if (empty) {
          out += empty;
          empty = 0;
        }
        const t = p > 0 ? p : -p;
        const letter = LETTERS[baseType(t)];
        out += (t > 8 ? '+' : '') + (p > 0 ? letter : letter.toLowerCase());
      }
      if (empty) out += empty;
      rows.push(out);
    }
    let hand = '';
    for (let si = 0; si < 2; si++) {
      for (const t of SFEN_HAND_ORDER) {
        const n = this._hand[si][t];
        if (!n) continue;
        const letter = si === 0 ? LETTERS[t] : LETTERS[t].toLowerCase();
        hand += (n > 1 ? n : '') + letter;
      }
    }
    const moveNo = this._moveNo0 + this._moveHistory.length;
    return `${rows.join('/')} ${this._stm > 0 ? 'b' : 'w'} ${hand || '-'} ${moveNo}`;
  }

  _recomputeHash() {
    let lo = 0;
    let hi = 0;
    for (let i = 0; i < 81; i++) {
      const p = this._sq[i];
      if (p) {
        lo ^= ZP_LO[zpi(i, p)];
        hi ^= ZP_HI[zpi(i, p)];
      }
    }
    for (let si = 0; si < 2; si++) {
      for (let t = 1; t <= 7; t++) {
        const n = Math.min(19, this._hand[si][t]);
        lo ^= ZH_LO[zhi(si, t, n)];
        hi ^= ZH_HI[zhi(si, t, n)];
      }
    }
    if (this._stm < 0) {
      lo ^= ZS_LO;
      hi ^= ZS_HI;
    }
    this._hlo = lo;
    this._hhi = hi;
  }

  // 53 bitlik pozisyon anahtarı (transpozisyon tablosu / tekrar tespiti için)
  hashKey() {
    return (this._hhi & 0x1fffff) * 4294967296 + (this._hlo >>> 0);
  }

  // ------------------------------------------------------------------ Durum

  board() {
    const out = [];
    for (let r = 0; r < 9; r++) {
      const row = [];
      for (let c = 0; c < 9; c++) {
        const p = this._sq[r * 9 + c];
        if (!p) {
          row.push(null);
          continue;
        }
        const t = p > 0 ? p : -p;
        row.push({ type: LETTERS[baseType(t)], color: p > 0 ? 'sente' : 'gote', promoted: t > 8 });
      }
      out.push(row);
    }
    return out;
  }

  hands() {
    const h = (si) => {
      const o = {};
      for (const k of HAND_TYPES) o[k] = this._hand[si][TYPE_INDEX[k]];
      return o;
    };
    return { sente: h(0), gote: h(1) };
  }

  turn() {
    return colorOf(this._stm);
  }

  history() {
    return [...this._moveHistory];
  }

  pieceAt({ row, col }) {
    const p = this._sq[row * 9 + col];
    if (!p) return null;
    const t = p > 0 ? p : -p;
    return { type: LETTERS[baseType(t)], color: p > 0 ? 'sente' : 'gote', promoted: t > 8 };
  }

  // ------------------------------------------------------------------ Saldırı

  // (r, c) karesine `by` (1 sente / -1 gote) tarafından saldırılıyor mu?
  _attacked(r, c, by) {
    const B = this._sq;
    const f = by > 0 ? -1 : 1; // saldıranın ileri yönü (row farkı)
    const nr = r - 2 * f;
    if (nr >= 0 && nr <= 8) {
      const kn = 3 * by;
      if (c > 0 && B[nr * 9 + c - 1] === kn) return true;
      if (c < 8 && B[nr * 9 + c + 1] === kn) return true;
    }
    for (let d = 0; d < 8; d++) {
      const a = RAY_DIRS[d][0];
      const b = RAY_DIRS[d][1];
      let rr = r + a;
      let cc = c + b;
      let k = 1;
      while (rr >= 0 && rr <= 8 && cc >= 0 && cc <= 8) {
        const p = B[rr * 9 + cc];
        if (p !== 0) {
          if ((p > 0) === (by > 0)) {
            const t = p > 0 ? p : -p;
            if (k === 1) {
              if (STEP_HIT[t * 9 + (-a * f + 1) * 3 + (-b + 1)]) return true;
            } else if (a !== 0 && b !== 0) {
              if (t === 6 || t === 14) return true;
            } else if (t === 7 || t === 15 || (t === 2 && a === -f)) {
              return true;
            }
          }
          break;
        }
        rr += a;
        cc += b;
        k++;
      }
    }
    return false;
  }

  // Genel: { row, col } karesine `byColor` saldırıyor mu?
  isSquareAttacked({ row, col }, byColor) {
    return this._attacked(row, col, sideOf(byColor));
  }

  _inCheck(side) {
    const k = this._king[side > 0 ? 0 : 1];
    if (k < 0) return false;
    return this._attacked((k / 9) | 0, k % 9, -side);
  }

  isCheck(color) {
    return this._inCheck(color ? sideOf(color) : this._stm);
  }

  // ------------------------------------------------------------------ Hamle üretimi

  _genPiece(from, side, out, capturesOnly) {
    const B = this._sq;
    const p = B[from];
    const t = p > 0 ? p : -p;
    const r = (from / 9) | 0;
    const c = from % 9;
    const f = side > 0 ? -1 : 1;
    const promotable = isPromotable(t);
    const fromZone = inZone(side, r);
    const add = (to, tr) => {
      if (promotable && (fromZone || inZone(side, tr))) {
        out.push(from | (to << 7) | (1 << 14));
        if (!isDeadSquare(t, side, tr)) out.push(from | (to << 7));
      } else {
        out.push(from | (to << 7));
      }
    };
    const steps = STEPS[t];
    for (let i = 0; i < steps.length; i++) {
      const tr = r + steps[i][0] * f;
      const tc = c + steps[i][1];
      if (tr < 0 || tr > 8 || tc < 0 || tc > 8) continue;
      const to = tr * 9 + tc;
      const q = B[to];
      if (q !== 0 && (q > 0) === (side > 0)) continue;
      if (capturesOnly && q === 0) continue;
      add(to, tr);
    }
    const slides = SLIDES[t];
    for (let i = 0; i < slides.length; i++) {
      const dr = slides[i][0] * f;
      const dc = slides[i][1];
      let tr = r + dr;
      let tc = c + dc;
      while (tr >= 0 && tr <= 8 && tc >= 0 && tc <= 8) {
        const to = tr * 9 + tc;
        const q = B[to];
        if (q !== 0) {
          if ((q > 0) !== (side > 0)) add(to, tr);
          break;
        }
        if (!capturesOnly) add(to, tr);
        tr += dr;
        tc += dc;
      }
    }
  }

  _genDrops(side, out, onlyType) {
    const B = this._sq;
    const hand = this._hand[side > 0 ? 0 : 1];
    let pawnFiles = 0;
    if (hand[1] > 0 && (!onlyType || onlyType === 1)) {
      const pc = side;
      for (let i = 0; i < 81; i++) if (B[i] === pc) pawnFiles |= 1 << i % 9;
    }
    for (let t = 1; t <= 7; t++) {
      if (!hand[t] || (onlyType && onlyType !== t)) continue;
      const base = (81 + t) | 0;
      for (let to = 0; to < 81; to++) {
        if (B[to] !== 0) continue;
        if (t <= 3) {
          const tr = (to / 9) | 0;
          if (isDeadSquare(t, side, tr)) continue;
          if (t === 1 && pawnFiles & (1 << to % 9)) continue;
        }
        out.push(base | (to << 7));
      }
    }
  }

  // Sözde-yasal tüm hamleler (tamsayı kodları)
  _gen(side, out, capturesOnly) {
    const B = this._sq;
    for (let i = 0; i < 81; i++) {
      const p = B[i];
      if (p !== 0 && (p > 0) === (side > 0)) this._genPiece(i, side, out, capturesOnly);
    }
    if (!capturesOnly) this._genDrops(side, out, 0);
    return out;
  }

  // ------------------------------------------------------------------ Yap / geri al

  _make(m, side) {
    const B = this._sq;
    const from = m & 127;
    const to = (m >> 7) & 127;
    const si = side > 0 ? 0 : 1;
    let lo = this._hlo;
    let hi = this._hhi;
    let cap = 0;
    if (from >= 81) {
      const t = from - 81;
      const hand = this._hand[si];
      const n = hand[t];
      lo ^= ZH_LO[zhi(si, t, n)] ^ ZH_LO[zhi(si, t, n - 1)];
      hi ^= ZH_HI[zhi(si, t, n)] ^ ZH_HI[zhi(si, t, n - 1)];
      hand[t] = n - 1;
      const code = t * side;
      B[to] = code;
      lo ^= ZP_LO[zpi(to, code)];
      hi ^= ZP_HI[zpi(to, code)];
    } else {
      const p = B[from];
      cap = B[to];
      lo ^= ZP_LO[zpi(from, p)];
      hi ^= ZP_HI[zpi(from, p)];
      B[from] = 0;
      if (cap !== 0) {
        lo ^= ZP_LO[zpi(to, cap)];
        hi ^= ZP_HI[zpi(to, cap)];
        const ct = baseType(cap > 0 ? cap : -cap);
        if (ct === 8) {
          this._king[1 - si] = -1;
        } else {
          const hand = this._hand[si];
          const n = hand[ct];
          if (n < 19) {
            lo ^= ZH_LO[zhi(si, ct, n)] ^ ZH_LO[zhi(si, ct, n + 1)];
            hi ^= ZH_HI[zhi(si, ct, n)] ^ ZH_HI[zhi(si, ct, n + 1)];
          }
          hand[ct] = n + 1;
        }
      }
      const np = (m >> 14) & 1 ? p + (p > 0 ? PROM : -PROM) : p;
      B[to] = np;
      lo ^= ZP_LO[zpi(to, np)];
      hi ^= ZP_HI[zpi(to, np)];
      if (p === 8 || p === -8) this._king[si] = to;
    }
    this._stack.push(m, cap, this._stm, side, this._hlo, this._hhi);
    this._stm = -this._stm;
    this._hlo = lo ^ ZS_LO;
    this._hhi = hi ^ ZS_HI;
  }

  _unmake() {
    const st = this._stack;
    const hhi = st.pop();
    const hlo = st.pop();
    const side = st.pop();
    const stm = st.pop();
    const cap = st.pop();
    const m = st.pop();
    const B = this._sq;
    const from = m & 127;
    const to = (m >> 7) & 127;
    const si = side > 0 ? 0 : 1;
    if (from >= 81) {
      this._hand[si][from - 81]++;
      B[to] = 0;
    } else {
      const np = B[to];
      const p = (m >> 14) & 1 ? np - (np > 0 ? PROM : -PROM) : np;
      B[from] = p;
      B[to] = cap;
      if (p === 8 || p === -8) this._king[si] = from;
      if (cap !== 0) {
        const ct = baseType(cap > 0 ? cap : -cap);
        if (ct === 8) this._king[1 - si] = to;
        else this._hand[si][ct]--;
      }
    }
    this._stm = stm;
    this._hlo = hlo;
    this._hhi = hhi;
  }

  // _make sonrası çağrılır: hamle yapan `side` için hamle yasal mıydı?
  // (kendi şahı açıkta değil + atılan piyadeyle mat değil)
  _wasLegal(m, side) {
    if (this._inCheck(side)) return false;
    if ((m & 127) === 82) {
      // piyade atma: rakip şaha doğrudan şah çekiyor mu?
      const to = (m >> 7) & 127;
      const ek = this._king[side > 0 ? 1 : 0];
      if (ek >= 0 && ek === to + (side > 0 ? -9 : 9)) {
        if (!this._hasLegal(-side)) return false; // uchifuzume
      }
    }
    return true;
  }

  _isLegalMove(m, side) {
    this._make(m, side);
    const ok = this._wasLegal(m, side);
    this._unmake();
    return ok;
  }

  _hasLegal(side) {
    const list = this._gen(side, [], false);
    for (let i = 0; i < list.length; i++) {
      if (this._isLegalMove(list[i], side)) return true;
    }
    return false;
  }

  _legalList(side) {
    const list = this._gen(side, [], false);
    const out = [];
    for (let i = 0; i < list.length; i++) if (this._isLegalMove(list[i], side)) out.push(list[i]);
    return out;
  }

  // ------------------------------------------------------------------ Genel hamle API'si

  move({ from, to, promote = false }) {
    const fi = from.row * 9 + from.col;
    const ti = to.row * 9 + to.col;
    const p = this._sq[fi];
    if (!p) throw new Error('Seçilen karede taş bulunamadı');
    const t = p > 0 ? p : -p;
    const side = p > 0 ? 1 : -1;
    const pr = promote && isPromotable(t) ? 1 : 0;
    const cap = this._sq[ti];
    if (cap !== 0 && (cap > 0) === (p > 0)) throw new Error('Kendi taşınızı alamazsınız');
    this._make(encodeMove(fi, ti, pr), side);
    const letter = LETTERS[baseType(t)];
    const moveRecord = {
      type: 'move',
      color: colorOf(side),
      from: { row: from.row, col: from.col },
      to: { row: to.row, col: to.col },
      piece: letter,
      pieceType: letter,
      symbol: PIECE_NAMES[letter] || letter,
      promoted: !!(pr || t > 8),
      captured: cap ? LETTERS[baseType(cap > 0 ? cap : -cap)] : null,
      promote: !!pr, // yalnız bu hamlede terfi edildiyse true
    };
    this._moveHistory.push(moveRecord);
    return moveRecord;
  }

  drop({ type, row, col, color }) {
    const playerColor = color || this.turn();
    const side = sideOf(playerColor);
    const t = TYPE_INDEX[type];
    if (!t || t === 8 || this._hand[side > 0 ? 0 : 1][t] <= 0) {
      throw new Error('Elinizde bu taştan kalmadı');
    }
    const ti = row * 9 + col;
    if (this._sq[ti]) throw new Error('Hedef kare boş değil');
    this._make(encodeMove(81 + t, ti, 0), side);
    const dropRecord = {
      type: 'drop',
      color: playerColor,
      piece: type,
      pieceType: type,
      symbol: PIECE_NAMES[type] || type,
      to: { row, col },
    };
    this._moveHistory.push(dropRecord);
    return dropRecord;
  }

  // Son genel hamleyi geri alır; geri alınan kaydı (ya da null) döner.
  undo() {
    if (!this._moveHistory.length || this._stack.length < 6) return null;
    this._unmake();
    return this._moveHistory.pop();
  }

  // Sözde-yasal atma kareleri (nifu + son sıra kısıtı). Geriye uyum için korunuyor.
  getLegalDrops(pieceType, color) {
    const side = color ? sideOf(color) : this._stm;
    const t = TYPE_INDEX[pieceType];
    if (!t || t === 8) return [];
    const B = this._sq;
    let pawnFiles = 0;
    if (t === 1) for (let i = 0; i < 81; i++) if (B[i] === side) pawnFiles |= 1 << i % 9;
    const legal = [];
    for (let to = 0; to < 81; to++) {
      if (B[to] !== 0) continue;
      const r = (to / 9) | 0;
      if (isDeadSquare(t, side, r)) continue;
      if (t === 1 && pawnFiles & (1 << to % 9)) continue;
      legal.push({ row: r, col: to % 9 });
    }
    return legal;
  }

  dropTargets(pieceType, color) {
    return this.getLegalDrops(pieceType, color);
  }

  // Yasal atma kareleri: nifu, son sıra, kendi şahını açıkta bırakmama, uchifuzume.
  legalDrops(pieceType, color) {
    const side = color ? sideOf(color) : this._stm;
    const t = TYPE_INDEX[pieceType];
    if (!t || t === 8 || this._hand[side > 0 ? 0 : 1][t] <= 0) return [];
    const list = [];
    this._genDrops(side, list, t);
    const out = [];
    for (const m of list) {
      if (this._isLegalMove(m, side)) {
        const to = (m >> 7) & 127;
        out.push({ row: (to / 9) | 0, col: to % 9 });
      }
    }
    return out;
  }

  _promoInfo(t, side, fr, tr) {
    const canPromote = isPromotable(t) && (inZone(side, fr) || inZone(side, tr));
    return { canPromote, mustPromote: canPromote && isDeadSquare(t, side, tr) };
  }

  // Sözde-yasal hedefler ({ to, capture, canPromote, mustPromote }). Geriye uyum için korunuyor.
  moves({ row, col }) {
    const fi = row * 9 + col;
    const p = this._sq[fi];
    if (!p) return [];
    const side = p > 0 ? 1 : -1;
    const t = p > 0 ? p : -p;
    const list = [];
    this._genPiece(fi, side, list, false);
    const seen = new Set();
    const out = [];
    for (const m of list) {
      const to = (m >> 7) & 127;
      if (seen.has(to)) continue;
      seen.add(to);
      const tr = (to / 9) | 0;
      out.push({ to: { row: tr, col: to % 9 }, capture: this._sq[to] !== 0, ...this._promoInfo(t, side, row, tr) });
    }
    return out;
  }

  // Yasal hedefler: moves() ile aynı şekil + canPromote/mustPromote; kendi şahını
  // şahta bırakan hamleler elenmiş.
  legalMoves({ row, col }) {
    const fi = row * 9 + col;
    const p = this._sq[fi];
    if (!p) return [];
    const side = p > 0 ? 1 : -1;
    const t = p > 0 ? p : -p;
    const list = [];
    this._genPiece(fi, side, list, false);
    const seen = new Set();
    const out = [];
    for (const m of list) {
      const to = (m >> 7) & 127;
      if (seen.has(to)) continue;
      // terfi seçeneğinden bağımsız olarak şah güvenliği aynıdır
      if (!this._isLegalMove(m, side)) continue;
      seen.add(to);
      const tr = (to / 9) | 0;
      out.push({ to: { row: tr, col: to % 9 }, capture: this._sq[to] !== 0, ...this._promoInfo(t, side, row, tr) });
    }
    return out;
  }

  hasAnyLegalMoves(color) {
    return this._hasLegal(color ? sideOf(color) : this._stm);
  }

  isCheckmate(color) {
    const side = color ? sideOf(color) : this._stm;
    return this._inCheck(side) && !this._hasLegal(side);
  }

  // Tüm yasal hamleler, tanımlayıcı olarak.
  allLegalMoves(color) {
    const side = color ? sideOf(color) : this._stm;
    return this._legalList(side).map((m) => this.descriptorFromCode(m));
  }

  // ------------------------------------------------------------------ Tanımlayıcılar / USI

  descriptorFromCode(m) {
    const from = m & 127;
    const to = (m >> 7) & 127;
    const toPos = { row: (to / 9) | 0, col: to % 9 };
    if (from >= 81) return { kind: 'drop', pieceType: LETTERS[from - 81], to: toPos };
    return { kind: 'move', from: { row: (from / 9) | 0, col: from % 9 }, to: toPos, promote: !!((m >> 14) & 1) };
  }

  // Tanımlayıcıyı iç hamle koduna çevirir (terfi bayrağı normalleştirilir). Geçersizse -1.
  codeFromDescriptor(d) {
    if (!d || !d.to) return -1;
    const ti = d.to.row * 9 + d.to.col;
    if (d.kind === 'drop') {
      const t = TYPE_INDEX[d.pieceType || d.type];
      if (!t || t === 8) return -1;
      return encodeMove(81 + t, ti, 0);
    }
    if (!d.from) return -1;
    const fi = d.from.row * 9 + d.from.col;
    const p = this._sq[fi];
    if (!p) return -1;
    const t = p > 0 ? p : -p;
    return encodeMove(fi, ti, d.promote && isPromotable(t) ? 1 : 0);
  }

  // Tanımlayıcı sıradaki oyuncu için yasal mı?
  isLegal(d) {
    const m = this.codeFromDescriptor(d);
    if (m < 0) return false;
    const side = this._stm;
    const list = [];
    const from = m & 127;
    if (from >= 81) this._genDrops(side, list, from - 81);
    else {
      const p = this._sq[from];
      if (!p || (p > 0) !== (side > 0)) return false;
      this._genPiece(from, side, list, false);
    }
    if (!list.includes(m)) return false;
    return this._isLegalMove(m, side);
  }

  // Tanımlayıcıyı (yasallığını kontrol ederek) oynar ve hamle kaydını döner.
  applyDescriptor(d) {
    if (!this.isLegal(d)) throw new Error('Yasadışı hamle');
    if (d.kind === 'drop') {
      return this.drop({ type: d.pieceType || d.type, row: d.to.row, col: d.to.col });
    }
    return this.move({ from: d.from, to: d.to, promote: !!d.promote });
  }

  static parseUsi(str) {
    const s = String(str || '').trim();
    const sq = (file, rank) => {
      const f = Number(file);
      const r = rank.charCodeAt(0) - 97;
      if (!(f >= 1 && f <= 9) || r < 0 || r > 8) return null;
      return { row: r, col: 9 - f };
    };
    let m = /^([PLNSGBR])\*([1-9])([a-i])$/.exec(s);
    if (m) {
      const to = sq(m[2], m[3]);
      return to ? { kind: 'drop', pieceType: m[1], to } : null;
    }
    m = /^([1-9])([a-i])([1-9])([a-i])(\+?)$/.exec(s);
    if (m) {
      const from = sq(m[1], m[2]);
      const to = sq(m[3], m[4]);
      return from && to ? { kind: 'move', from, to, promote: m[5] === '+' } : null;
    }
    return null;
  }

  static toUsi(d) {
    if (!d || !d.to) return '';
    const sq = (p) => `${9 - p.col}${String.fromCharCode(97 + p.row)}`;
    if (d.kind === 'drop') return `${d.pieceType || d.type}*${sq(d.to)}`;
    return `${sq(d.from)}${sq(d.to)}${d.promote ? '+' : ''}`;
  }

  parseUsi(str) {
    return Shogi.parseUsi(str);
  }

  toUsi(d) {
    return Shogi.toUsi(d);
  }

  unpromotedType(type) {
    const map = {
      '+P': 'P',
      '+L': 'L',
      '+N': 'N',
      '+S': 'S',
      '+B': 'B',
      '+R': 'R',
    };
    return map[type] || type;
  }
}

export const parseUsi = Shogi.parseUsi;
export const toUsi = Shogi.toUsi;
